import React, { memo } from "react";
import { PlanningApplicationSearchState } from "../../pages/search/PlanningApplication/PlanningApplicationSearchState";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { SearchHelper } from "../../pages/search/SearchHelper";
import PlanningApplicationSimpleSearch from "../../pages/search/PlanningApplication/PlanningApplicationSimpleSearch";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PlanningApplicationsFilter } from "@shared/publicportal/contracts/PlanningApplicationsFilter";
import "../../pages/search/Search.css";
import datePickerValueFormatter from "@mirage/components/helpers/datePickerValueFormatter";
import "./AdvancedSearch.css";

interface PlanningApplicationAdvSearchProps {
    state: PlanningApplicationSearchState;
    setter: (modifier: Partial<PlanningApplicationsFilter>) => void;
}

const PlanningApplicationAdvancedSearch: React.FC<PlanningApplicationAdvSearchProps> = memo((props) => {
    return (
        <>
            <PlanningApplicationSimpleSearch setter={props.setter} state={props.state} />
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "70%" } }}>
                <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                    <Typography variant="h5">Details</Typography>
                </Grid>
                {getDetailFields().map((field) => {
                    return (
                        <Grid size={{ xs: 12, md: 6, xl: 4 }} key={"p-details-" + field.key}>
                            {field}
                        </Grid>
                    );
                })}
                <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                    <TextField
                        value={props.state.filter.address}
                        onChange={(e) => props.setter({ address: e.target.value })}
                        key="app-address-key"
                        size="small"
                        id="app-address"
                        label="Address"
                        variant="outlined"
                        className="advancedSearchAddressField"
                    />
                </Grid>
            </Grid>
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "45%" } }}>
                <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                    <Typography variant="h5">Date Fields</Typography>
                </Grid>
                {getDateFields().map((field) => {
                    return (
                        <Grid size={{ xs: 12, md: 6, xl: 6 }} key={"p-dates-" + field.key}>
                            {field}
                        </Grid>
                    );
                })}
            </Grid>
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "45%" } }}></Grid>
        </>
    );

    function getDetailFields() {
        return [
            <TextField
                value={props.state.filter.applicant}
                onChange={(e) => props.setter({ applicant: e.target.value })}
                key="app-applicant-key"
                size="small"
                id="app-applicant"
                label="Applicant"
                variant="outlined"
                className="min-width-18rem"
            />,
            <TextField
                value={props.state.filter.agent}
                onChange={(e) => props.setter({ agent: e.target.value })}
                key="app-agent-key"
                size="small"
                id="app-agent"
                label="Agent"
                variant="outlined"
                className="min-width-18rem"
            />,
            <Autocomplete
                options={props.state.appTypes || []}
                id="app-appTypes"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                key="app-appTypes-key"
                size="small"
                value={props.state.filter.appType?.length ? props.state.filter.appType : null}
                inputValue={props.state.filter.appType}
                onInputChange={(event, value) => event && props.setter({ appType: value })}
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Application Type")}
            />,
            <Autocomplete
                options={props.state.decisions || []}
                id="app-decisions"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                key="app-decisions-key"
                size="small"
                value={props.state.filter.decision?.length ? props.state.filter.decision : null}
                inputValue={props.state.filter.decision}
                onInputChange={(event, value) => event && props.setter({ decision: value })}
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Decision")}
            />,
            <Autocomplete
                options={props.state.wards || []}
                inputValue={props.state.filter.ward}
                value={props.state.filter.ward?.length ? props.state.filter.ward : null}
                onInputChange={(event, value) => event && props.setter({ ward: value })}
                key="app-ward-key"
                size="small"
                id="app-ward"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Ward")}
            />,
            <Autocomplete
                options={props.state.parishes || []}
                inputValue={props.state.filter.parish}
                value={props.state.filter.parish?.length ? props.state.filter.parish : null}
                onInputChange={(event, value) => event && props.setter({ parish: value })}
                key="app-parish-key"
                size="small"
                id="app-parish"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Parish")}
            />,
        ];
    }

    function getDateFields() {
        return [
            <LocalizationProvider dateAdapter={AdapterDayjs} key="app-Decision-From-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.decisionDateFrom)}
                    onChange={(value: Dayjs | null) => props.setter({ decisionDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("app-Decision-From") } }}
                    label="Decision Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="app-Decision-To-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.decisionDateTo)}
                    onChange={(value: Dayjs | null) => props.setter({ decisionDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("app-Decision-To") } }}
                    label="Decision Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="app-Received-Date-From-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.receivedDateFrom)}
                    onChange={(value: Dayjs | null) => props.setter({ receivedDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("app-Received-Deadline-From") } }}
                    label="Received Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="app-Received-Date-To-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.receivedDateTo)}
                    onChange={(value: Dayjs | null) => props.setter({ receivedDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("app-Received-Deadline-To") } }}
                    label="Received Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="app-Validated-Date-From-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.validatedDateFrom)}
                    onChange={(value: Dayjs | null) => props.setter({ validatedDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("app-Validated-Date-From") } }}
                    label="Valid Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="app-Validated-Date-To-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.validatedDateTo)}
                    onChange={(value: Dayjs | null) => props.setter({ validatedDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("app-Validated-Date-To") } }}
                    label="Valid Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="app-Registered-Date-From-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.registeredDateFrom)}
                    onChange={(value: Dayjs | null) => props.setter({ registeredDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("app-Registered-Date-From") } }}
                    label="Registered Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="app-Registered-Date-To-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.registeredDateTo)}
                    onChange={(value: Dayjs | null) => props.setter({ registeredDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("app-Registered-Date-To") } }}
                    label="Registered Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="app-Committee-Date-From-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.committeeDateFrom)}
                    onChange={(value: Dayjs | null) => props.setter({ committeeDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("app-Committee-Date-From") } }}
                    label="Committee Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="app-Committee-Date-To-key">
                <DatePicker
                    value={datePickerValueFormatter(props.state.filter.committeeDateTo)}
                    onChange={(value: Dayjs | null) => props.setter({ committeeDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("app-Committee-Date-To") } }}
                    label="Committee Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
        ];
    }
});

export default PlanningApplicationAdvancedSearch;
